import { Separator } from '@/components/primitives/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetMain,
  SheetTitle,
} from '@/components/primitives/sheet';
import { Skeleton } from '@/components/primitives/skeleton';
import { useAiConnection } from '@/hooks/use-ai-connections';
import { formatDate } from '@/utils/format-date';

type ConnectionDetailSheetProps = {
  connectionId: string | undefined;
  onClose: () => void;
};

export function ConnectionDetailSheet({ connectionId, onClose }: ConnectionDetailSheetProps) {
  const { data: connection, isLoading } = useAiConnection(connectionId);

  return (
    <Sheet open={Boolean(connectionId)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <SheetHeader>
          <SheetTitle>{connection?.clientName ?? 'Connection'}</SheetTitle>
          <SheetDescription>Where this app connected from and what it changed.</SheetDescription>
        </SheetHeader>
        <Separator />
        <SheetMain className="flex flex-col gap-6">
          {isLoading || !connection ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <>
              <dl className="text-paragraph-xs grid grid-cols-[120px_1fr] gap-x-3 gap-y-2">
                <dt className="text-text-soft">Connected</dt>
                <dd className="text-text-sub">{formatDate(connection.createdAt)}</dd>
                <dt className="text-text-soft">Approved from</dt>
                <dd className="text-text-sub break-all">
                  {connection.approvedIp ?? '—'}
                  {connection.approvedUserAgent && (
                    <span className="text-text-soft block">{connection.approvedUserAgent}</span>
                  )}
                </dd>
                <dt className="text-text-soft">Last used</dt>
                <dd className="text-text-sub">{connection.lastUsedAt ? formatDate(connection.lastUsedAt) : 'Never'}</dd>
                <dt className="text-text-soft">Last used from</dt>
                <dd className="text-text-sub break-all">
                  {connection.lastIp ?? '—'}
                  {connection.lastUserAgent && <span className="text-text-soft block">{connection.lastUserAgent}</span>}
                </dd>
                <dt className="text-text-soft">Tool calls</dt>
                <dd className="text-text-sub">{connection.callCount}</dd>
                {connection.revokedAt && (
                  <>
                    <dt className="text-text-soft">Revoked</dt>
                    <dd className="text-text-sub">{formatDate(connection.revokedAt)}</dd>
                  </>
                )}
              </dl>

              <div className="flex flex-col gap-2">
                <span className="text-label-sm text-text-strong">Recent edits</span>
                {connection.recentEdits.length === 0 ? (
                  <span className="text-paragraph-xs text-text-soft">This app hasn't changed anything.</span>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {connection.recentEdits.map((edit) => (
                      <li key={edit.id} className="rounded-lg border border-neutral-100 p-2">
                        <div className="text-label-xs text-text-strong">
                          {edit.workflowId ?? edit.layoutId}
                          {edit.stepId && <span className="text-text-soft"> · {edit.stepId}</span>}
                        </div>
                        <div className="text-paragraph-xs text-text-sub">{edit.summary}</div>
                        <div className="text-paragraph-2xs text-text-soft">
                          {edit.environmentName} · {formatDate(edit.at)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </SheetMain>
      </SheetContent>
    </Sheet>
  );
}
